import type { AdminProjectAccessProject } from '@frontend/types';
import { buildProjectTree, type ProjectTreeNode } from '../utils/buildProjectTree';

export interface RealmEcosystemProjectsTableProps {
  projects: AdminProjectAccessProject[];
  selectedProjectId: string | null;
  onSelectTopLevel: (project: AdminProjectAccessProject) => void;
  loading?: boolean;
  error?: string;
}

type FlatRow = {
  project: AdminProjectAccessProject;
  depth: number;
  isRoot: boolean;
};

function flattenTree(
  nodes: ProjectTreeNode<AdminProjectAccessProject>[],
  depth = 0
): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const node of nodes) {
    rows.push({ project: node.project, depth, isRoot: node.isRoot });
    if (node.children.length) {
      rows.push(...flattenTree(node.children, depth + 1));
    }
  }
  return rows;
}

export default function RealmEcosystemProjectsTable({
  projects,
  selectedProjectId,
  onSelectTopLevel,
  loading,
  error,
}: RealmEcosystemProjectsTableProps) {
  if (loading) {
    return <p className="ecosystem-description">Loading projects…</p>;
  }
  if (error) {
    return (
      <div className="settings-message settings-error" role="alert">
        {error}
      </div>
    );
  }
  if (projects.length === 0) {
    return <p className="ecosystem-description">No projects in this ecosystem.</p>;
  }

  const rows = flattenTree(buildProjectTree(projects));

  return (
    <div className="admin-orch-table-wrap">
      <table className="admin-orch-table" aria-label="Projects in ecosystem">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Id</th>
            <th scope="col">Parent</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ project, depth, isRoot }) => {
            const selected = selectedProjectId === project.id;
            const parent = project.parentProjectId?.trim() || '';
            if (isRoot) {
              return (
                <tr
                  key={project.id}
                  className={selected ? 'is-selected' : ''}
                  onClick={() => onSelectTopLevel(project)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectTopLevel(project);
                    }
                  }}
                  aria-selected={selected}
                >
                  <td
                    className="admin-orch-table-name"
                    style={{ paddingLeft: `${0.75 + depth * 1.1}rem` }}
                  >
                    {project.name || project.id}
                  </td>
                  <td>
                    <code className="admin-inline-code">{project.id}</code>
                  </td>
                  <td className="admin-orch-table-muted">—</td>
                </tr>
              );
            }
            return (
              <tr
                key={project.id}
                className="is-child"
                title="Sub-projects cannot be moved from Realm Admin"
              >
                <td
                  className="admin-orch-table-name"
                  style={{ paddingLeft: `${0.75 + depth * 1.1}rem` }}
                >
                  {project.name || project.id}
                </td>
                <td>
                  <code className="admin-inline-code">{project.id}</code>
                </td>
                <td>
                  {parent ? (
                    <code className="admin-inline-code">{parent}</code>
                  ) : (
                    <span className="admin-orch-table-muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
